import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { Circle2d } from '@/editor/math/circle2d';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Line2d } from '../../math/Line2d';
import { Point2dUtils } from '../../math/Point2dUtils';
import { Ray2d } from '@/editor/math/ray2d';
import { arcThresholdValue, thresholdValue } from '@/editor/math/const';
import { intersectCommonShape } from '@/editor/math/shape/intesection';
import { getTangentDirection } from '@/editor/math/shape/tangent';
import { getNearestPoint, normalizeAngle } from '@/editor/math/utils';
import { dot, opposite } from '@/editor/math/vector';
import { IShapePoint2d, IShapeSeg2d } from '@/editor/types/shape';
import { IWallInfo } from '@/editor/types/wallShape';
import { HandleType } from '../wallShape/calculateHelper';

// ============================================================================
// 端点与方向
// ============================================================================

/** 本次处理的那一端的端点：Start 端取 seg.start，End 端取 seg.end。 */
export function endOf(seg: IShapeSeg2d, type: HandleType): IPoint2d {
  return type === HandleType.Start ? seg.start : seg.end;
}

/** 本次处理那一端的【对端】端点(墙本体方向所指的那头)。 */
export function farEndOf(seg: IShapeSeg2d, type: HandleType): IPoint2d {
  return type === HandleType.End ? seg.start : seg.end;
}

/** 从接头端点出发、指向墙本体的单位方向：弧取该端点处的切向，直墙取两端点连线方向。 */
export function bodyDirectionOf(seg: IShapeSeg2d, type: HandleType): IPoint2d {
  if (seg instanceof ArcSeg2d) {
    // getTangentDirection 给的是沿扫向的行进方向：起点处即指向本体，终点处需反向。
    const t = getTangentDirection(seg, endOf(seg, type));
    return type === HandleType.Start ? t : opposite(t);
  }
  return Point2dUtils.getUnitDirection(endOf(seg, type), farEndOf(seg, type));
}

/**
 * seg 在其端点 junction 处「离开接头、指向本体」的单位方向(弧取切向、直墙取走向)。
 * junction 不是该边的端点时返回 null。
 */
export function outwardDirectionAt(seg: IShapeSeg2d, junction: IPoint2d): IPoint2d | null {
  const atStart = Point2dUtils.equals(seg.start, junction);
  if (!atStart && !Point2dUtils.equals(seg.end, junction)) return null;
  // getTangentDirection 给的是沿走向的行进方向：起点处即离开接头，终点处需反向。
  const t = getTangentDirection(seg, junction);
  return atStart ? t : opposite(t);
}

// ============================================================================
// 点与线段的基本量
// ============================================================================

/** 点 p 对有向线段 a→b 的叉积(未归一化，量级随 |ab| 放大，只可用于比较符号)。 */
export function crossOf(a: IPoint2d, b: IPoint2d, p: IPoint2d): number {
  return (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
}

/** 点 p 到直线 a→b 的有符号垂距(叉积按 |ab| 归一化，可直接与长度阈值比较)。 */
export function signedDistanceToLine(a: IPoint2d, b: IPoint2d, p: IPoint2d): number {
  return crossOf(a, b, p) / (Point2dUtils.distanceTo(a, b) || 1);
}

/** 点 p 在线段 a→b 上的归一化投影参数：0=a，1=b；线段退化时返回 -1。 */
export function projT(p: IPoint2d, a: IPoint2d, b: IPoint2d): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  return l2 > 0 ? ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2 : -1;
}

/** 点 p 相对 origin 沿单位方向 dir 的投影距离(正=前方)。 */
export function projectAlong(p: IPoint2d, origin: IPoint2d, dir: IPoint2d): number {
  return (p.x - origin.x) * dir.x + (p.y - origin.y) * dir.y;
}

/** 三点是否近似共线(偏移量 < 相对容差 × 首末点距离)。 */
export function isCollinear(a: IPoint2d, b: IPoint2d, p: IPoint2d, relTol = 1e-3): boolean {
  return Math.abs(crossOf(a, b, p)) < relTol * Point2dUtils.distanceTo(a, b);
}

/** 两条边是否共用【两个】端点(弦与穹顶、透镜孪生等构型)。 */
export function sharesBothEndpoints(a: IShapeSeg2d, b: IShapeSeg2d): boolean {
  return (
    (Point2dUtils.equals(a.start, b.start) && Point2dUtils.equals(a.end, b.end)) ||
    (Point2dUtils.equals(a.start, b.end) && Point2dUtils.equals(a.end, b.start))
  );
}

/** 两条边是否共用一个端点，且该公共角点落在 ref 附近(≤reach)——筛「接头附近的一跳邻边」。 */
export function sharedCornerWithin(a: IShapeSeg2d, b: IShapeSeg2d, ref: IPoint2d, reach: number): boolean {
  for (const pa of [a.start, a.end]) {
    for (const pb of [b.start, b.end]) {
      if (Point2dUtils.equals(pa, pb)) return Point2dUtils.distanceTo(pa, ref) <= reach;
    }
  }
  return false;
}

/** junction 是否为该边的端点。 */
export function isEndpointOf(seg: IShapeSeg2d, junction: IPoint2d): boolean {
  return Point2dUtils.equals(seg.start, junction) || Point2dUtils.equals(seg.end, junction);
}

/**
 * 墙在端点 junction 那一头的「墙厚端盖面」：连接它在该端的左右两条偏移端点(垂直中线、宽=墙厚)。
 * junction 不是它的起点时一律按终点侧取，调用方需要严格性时先用 isEndpointOf 把关。
 */
export function endCapFaceAt(w: IWallInfo, junction: IPoint2d): Line2d {
  const atStart = Point2dUtils.equals(w.seg.start, junction);
  const l = atStart ? w.leftSeg.start : w.leftSeg.end;
  const r = atStart ? w.rightSeg.start : w.rightSeg.end;
  return new Line2d({ points: { p1: l, p2: r } });
}

/** 墙 w 朝参考点 ref(如弧的鼓点)那一侧的偏移边——两墙相贴时它就是「近边」。 */
export function nearOffsetSegOf(w: IWallInfo, centerline: Circle2d | Line2d, ref: IPoint2d): IShapeSeg2d {
  const sideRef = signedSideOfCenterline(ref, centerline);
  const sideLeft = signedSideOfCenterline(w.leftSeg.start, centerline);
  return sideRef * sideLeft >= 0 ? w.leftSeg : w.rightSeg;
}

/** 候选点里取「离 ref 最近、且在 reach 之内」的那个；一个都没有则退回 fallback。 */
export function nearestWithinReach(cands: IPoint2d[], ref: IPoint2d, reach: number, fallback: IPoint2d): IShapePoint2d {
  const near = cands.filter((p) => Point2dUtils.distanceTo(p, ref) <= reach);
  const chosen = (near.length ? getNearestPoint(near, ref) : null) ?? fallback;
  return { x: chosen.x, y: chosen.y };
}

/** 往阶梯端盖里追加一点：取不到、离接头超出 reach、或与上一点重合的都跳过。 */
export function pushCapPoint(cap: IShapePoint2d[], p: IPoint2d | null | undefined, junction: IPoint2d, reach: number) {
  if (!p || Point2dUtils.distanceTo(p, junction) > reach) return;
  const last = cap[cap.length - 1];
  if (last && Point2dUtils.distanceTo(last, p) <= thresholdValue) return;
  cap.push({ x: p.x, y: p.y });
}

// ============================================================================
// 形状的「延长形态」与弧段范围
// ============================================================================

/** 边的延长形态：弧 → 整圆，直线段 → 无限直线。求斜接角点/端盖交点时用它，再另行判段内。 */
export function extendSeg(seg: IShapeSeg2d): Circle2d | Line2d {
  return seg instanceof ArcSeg2d
    ? new Circle2d({ center: seg.center, radius: seg.radius })
    : new Line2d({ points: { p1: seg.start, p2: seg.end } });
}

/**
 * 点 p 相对弧段起点的扫角(近 2π 的回卷归到小负值)；超出 [−tol, sweep+tol] 时返回 null。
 * tol 按弧度给，调用方需要绝对长度闸时应自行折算(见 crossSeg 的 eTol)。
 */
export function sweepOnArc(seg: ArcSeg2d, p: IPoint2d, tol = arcThresholdValue): number | null {
  let s = seg.getPointSweepAngle(p);
  if (s > 2 * Math.PI - tol) s -= 2 * Math.PI;
  if (s < -tol || s > seg.getSweepAngle() + tol) return null;
  return s;
}

/** 点是否落在这条边的标称范围内：弧按扫角、直线按线段投影(都不用无限延长形态)。 */
export function onSegSpan(seg: IShapeSeg2d, p: IPoint2d, tol = arcThresholdValue): boolean {
  if (seg instanceof ArcSeg2d) return sweepOnArc(seg, p, tol) !== null;
  const t = projT(p, seg.start, seg.end);
  return t >= 0 && t <= 1;
}

// ============================================================================
// 位置判定
// ============================================================================

// 点是否【严格】落在给定边的标称范围内(不带容差)。crossSeg 为容纳接头处的正常相掠留了 junctionBand(0.01rad)
// 容差，几条弧在接头近乎相切时会因此收到「其实在弧段外」的幻影交点；需要区分真交点/幻影时用本判定。
export function strictlyOnSeg(seg: IShapeSeg2d, p: IPoint2d): boolean {
  if (seg instanceof ArcSeg2d) {
    const s = seg.getPointSweepAngle(p);
    return s >= 0 && s <= seg.getSweepAngle();
  }
  const dx = seg.end.x - seg.start.x;
  const dy = seg.end.y - seg.start.y;
  const len2 = dx * dx + dy * dy;
  if (len2 <= 0) return false;
  const t = ((p.x - seg.start.x) * dx + (p.y - seg.start.y) * dy) / len2;
  return t >= 0 && t <= 1;
}

// 两墙在共用端点 junction 处是否「近发夹」：各自从 junction 出发(离开接头)的切线方向夹角 < 20°，
// 即两条墙带从接头起几乎同向重叠、要到很远(≈墙厚/sin(夹角))才分开 —— 斜接点会落在距离闸之外。
export function isNearHairpinJoint(mainWallInfo: IWallInfo, otherWallInfo: IWallInfo, junction: IPoint2d): boolean {
  const mainDirection = outwardDirectionAt(mainWallInfo.seg, junction);
  const otherDirection = outwardDirectionAt(otherWallInfo.seg, junction);
  if (!mainDirection || !otherDirection) return false;
  return dot(mainDirection, otherDirection) > Math.cos(Math.PI / 9);
}

// 点 p 相对中线 shape 的有符号侧：圆→到圆心距离-半径(正=外/负=内)；直线→叉积(两点取自中线，符号随画线方向，但与另一点相乘后方向无关)。
export function signedSideOfCenterline(p: IPoint2d, shape: Circle2d | Line2d): number {
  if (shape instanceof Circle2d) {
    return Point2dUtils.distanceTo(p, shape.center) - shape.radius;
  }
  const [a, b] = shape.getPoints();
  // 归一化为有符号垂距（与 Circle 分支单位一致），否则叉积量级随中线长度放大，
  // 落在中线上的点会因浮点噪声得到非零值，导致 thresholdValue 容差失效。
  const cross = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  return len === 0 ? cross : cross / len;
}

// 点 p 是否落在以 seg(中线) 为骨架、halfWidth 为半宽的墙体实心带「内部」(到中线垂距 < 半宽、且在 seg 跨度内)。
// 落在带边界(垂距≈半宽)不算内部，避免把落在某墙边线上的交点当成埋进它自己。
export function isInsideBand(p: IPoint2d, seg: IShapeSeg2d, halfWidth: number): boolean {
  if (seg instanceof ArcSeg2d) {
    if (Math.abs(Point2dUtils.distanceTo(p, seg.center) - seg.radius) >= halfWidth - thresholdValue) return false;
    const ang = normalizeAngle(Math.atan2(p.y - seg.center.y, p.x - seg.center.x));
    const diff = seg.clockwise ? normalizeAngle(seg.startAngle - ang) : normalizeAngle(ang - seg.startAngle);
    return diff <= seg.getSweepAngle() + thresholdValue;
  }
  const a = seg.start;
  const b = seg.end;
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 === 0) return false;
  const len = Math.sqrt(len2);
  const t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
  // 端部按方形端帽延伸半宽(墙体端部实心区含端帽)：连接角点处、从墙端沿垂向偏出的交点(投影略越过 t∈[0,1])
  // 仍应算作埋在该墙内，否则会被当成有效端盖顶点而漏切。
  const tMargin = halfWidth / len;
  if (t < -tMargin - thresholdValue || t > 1 + tMargin + thresholdValue) return false;
  const perp = Point2dUtils.distanceTo(p, { x: a.x + t * abx, y: a.y + t * aby });
  return perp < halfWidth - thresholdValue;
}

// 严格版「埋在墙体内」：不含 isInsideBand 的方形端帽外扩(tMargin)，投影必须落在中线跨度之内。
// 判「本墙偏移端点是不是真压在更早墙的材料里」必须用严格版：端帽外扩会把「落在对方端盖面身后几十毫米」也算成埋着，
// 从而把反侧楔形填充所需的身后斜接点一并禁掉(实测让接头处的填充整段消失、两墙间留出一个楔形缺口)。
export function isStrictlyInsideBand(p: IPoint2d, seg: IShapeSeg2d, halfWidth: number): boolean {
  // 落在 seg 自己的端点上不算埋着。直墙靠下面的 t∈(0,1) 排除，但那是零容差比较：存档坐标里共端点常差
  // 1e-13mm 量级的浮点噪声；弧分支委托的 isInsideBand 更是对弧自身两端恒为真，完全没有对应排除。
  if (Point2dUtils.equals(p, seg.start) || Point2dUtils.equals(p, seg.end)) return false;
  if (seg instanceof ArcSeg2d) return isInsideBand(p, seg, halfWidth); // 弧带本就没有端帽外扩
  const a = seg.start;
  const b = seg.end;
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 === 0) return false;
  const t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
  if (t <= 0 || t >= 1) return false;
  const perp = Point2dUtils.distanceTo(p, { x: a.x + t * abx, y: a.y + t * aby });
  return perp < halfWidth - thresholdValue;
}

// ============================================================================
// 端盖沿更早轮廓走时的弧标注
// ============================================================================

// 端盖上「沿更早墙的某条轮廓边从 point 走到 next」这一段：cutter 是弧时该段本身就是那条弧(布尔差的边界)，
// 拉成直线弦会切进弧的内侧 —— 若本墙自己的偏移弧恰好贴着这条弧(如两内偏移圆内切)，弦会横穿本墙自身的偏移弧，
// 整条墙轮廓自交(实测 463mm 的弦切进自身内弧 4.5mm)，下游 PathFinder 的面遍历会因此丢掉一个分区。
// IShapePoint2d 的 center/clockwise 描述「本点到下一点」那一段，getWallDrawShape / getSepWallSegs 都已支持，
// 故直接把该段标成弧。取两点间的劣弧(端盖是接头附近的局部段)；劣弧扫角过大(>90°)说明不是同一段局部边界，保持直线。
// 给端盖套上上面这条规则：逐段看「这一段的两个端点是不是都落在某条 cutter 边上」，是就标成沿那条边的弧。
// 顺序无关(方向由两点在圆上的劣弧定)，所以端盖排成 left→right 之后再套即可。所有产出「沿更早轮廓走」的端盖
// 分支(cutByEarlier 的并集角/同边两点、steppedKeepOther 的阶梯、calArcMultiCutByEarlier 的阶梯与转角)都要过这一道，
// 否则同一套方案换个画法命中另一条分支就又退化成弦(三条以上弧共点、含相切弧时尤其容易换分支)。
export function markCapArcs<T extends IShapePoint2d[] | null>(
  cap: T,
  main: IWallInfo,
  ...segs: (IShapeSeg2d | null | undefined)[]
): T {
  if (!cap || cap.length < 2) return cap;
  for (let i = 0; i + 1 < cap.length; i++) {
    if (cap[i].center) continue;
    for (const seg of segs) {
      if (!seg) continue;
      const marked = alongCutterArc(cap[i], seg, cap[i + 1], main);
      if (marked !== cap[i]) {
        cap[i] = marked;
        break;
      }
    }
  }
  return cap;
}

export function alongCutterArc(point: IShapePoint2d, seg: IShapeSeg2d, next: IPoint2d, main: IWallInfo): IShapePoint2d {
  if (!(seg instanceof ArcSeg2d)) return point;
  const c = seg.center;
  const angP = Math.atan2(point.y - c.y, point.x - c.x);
  const angN = Math.atan2(next.y - c.y, next.x - c.x);
  const cwSweep = normalizeAngle(angP - angN); // 顺时针从 point 到 next 的扫角
  const ccwSweep = normalizeAngle(angN - angP);
  const sweep = Math.min(cwSweep, ccwSweep);
  if (sweep < arcThresholdValue || sweep > Math.PI / 2) return point;
  // 弦高 <0.5mm 时弦与弧肉眼/数值上无异 —— 保持直线，不给下游平白多出弧数据(大半径 cutter 的绝大多数端盖都在此列)。
  if (seg.radius * (1 - Math.cos(sweep / 2)) < 0.5) return point;
  // 两点须都落在这条 cutter 的圆上(容差按弧度换算的弦高量级)，否则不是沿它走的。
  const dP = Math.abs(Point2dUtils.distanceTo(point, c) - seg.radius);
  const dN = Math.abs(Point2dUtils.distanceTo(next, c) - seg.radius);
  if (dP > 0.01 || dN > 0.01) return point;
  // 光「两端点同在这个圆上」不够：大半径 cutter 上相距较远的两点之间那段弧会鼓到本墙带外，而端盖是
  // 「本墙带 − 更早带」的边界、整段必在本墙带内。用弧中点验一次，鼓出带外就不是这条边界，保持直线。
  const midAng = cwSweep <= ccwSweep ? angP - cwSweep / 2 : angP + ccwSweep / 2;
  const mid = { x: c.x + seg.radius * Math.cos(midAng), y: c.y + seg.radius * Math.sin(midAng) };
  const half = main.thickness / 2 + 0.01;
  const offCenterline =
    main.center && main.shape instanceof Circle2d
      ? Math.abs(Point2dUtils.distanceTo(mid, main.center) - main.shape.radius)
      : Math.abs(
          (main.seg.end.x - main.seg.start.x) * (mid.y - main.seg.start.y) -
            (main.seg.end.y - main.seg.start.y) * (mid.x - main.seg.start.x)
        ) / (Point2dUtils.distanceTo(main.seg.start, main.seg.end) || 1);
  if (offCenterline > half) return point;
  return { ...point, center: { x: c.x, y: c.y }, clockwise: cwSweep <= ccwSweep };
}

export function rayIntersectSegments(ray2d: Ray2d, segments: IShapeSeg2d[]) {
  let minDistance = Infinity;
  let point;
  let index = -1;
  segments.forEach((seg, _index) => {
    // const intersect = intersectRayAndLineSeg(ray2d, seg);

    const intersect = intersectCommonShape(ray2d, seg);

    if (!intersect) return;

    const p = intersect[0]; // TODO: 待优化

    const distance = Point2dUtils.distanceTo(ray2d.origin, p);
    if (distance < minDistance) {
      minDistance = distance;
      point = p;
      index = _index;
    }
  });

  return point ? { point, index, distance: minDistance } : null;
}
